import { useEffect, useState } from 'react';
import CustomButton from '../reusable/CustomButton';
import useErrorModal from '../hooks/useErrorModal';
import { getChatInstructions, updateChatInstructions } from '../../services/chatService';

interface ChatInstructionsPanelProps {
  eoaId: string;
  accessToken: string;
}

const INSTRUCTIONS_MAX_LENGTH = 8000;

const ChatInstructionsPanel = ({ eoaId, accessToken }: ChatInstructionsPanelProps) => {
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const [instructions, setInstructions] = useState('');
  const [savedInstructions, setSavedInstructions] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const text = await getChatInstructions(eoaId, accessToken);
        if (!active) return;
        setInstructions(text);
        setSavedInstructions(text);
      } catch (error) {
        handleOpenErrorModal('Failed to load your instructions', error as Error);
      }
    })();
    return () => {
      active = false;
    };
  }, [eoaId, accessToken, handleOpenErrorModal]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const text = await updateChatInstructions(eoaId, accessToken, instructions);
      setInstructions(text);
      setSavedInstructions(text);
    } catch (error) {
      handleOpenErrorModal('Failed to save your instructions', error as Error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="chat-bottom-column chat-instructions-panel">
      <div className="chat-panel-heading">
        <h2 className="chat-panel-title">My instructions</h2>
        <p className="chat-panel-subtitle">Add instructions to tailor the responses you receive.</p>
      </div>
      <div className="chat-instructions-input">
        <textarea
          className="chat-instructions-textarea"
          value={instructions}
          onChange={(event) => setInstructions(event.target.value)}
          placeholder="Type here..."
          maxLength={INSTRUCTIONS_MAX_LENGTH}
        />
        <div className="chat-instructions-actions">
          <CustomButton
            variant="secondary"
            buttonText="Save"
            buttonAction={() => void handleSave()}
            disabled={instructions === savedInstructions}
            loading={saving}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatInstructionsPanel;
